// ── Game joining and state subscription ──

import { ref, get, update, onValue, type Unsubscribe } from 'firebase/database';
import { getFirebaseDb, getCurrentUid } from '../firebase';
import type { GameState, PlayerColor } from '../game/state';

/** Look up a game ID by room code */
export async function lookupRoomCode(code: string): Promise<string | null> {
  const db = getFirebaseDb();
  const snap = await get(ref(db, `roomCodes/${code.toUpperCase()}`));
  return snap.exists() ? (snap.val() as string) : null;
}

/** Join an existing game as player 2 */
export async function joinGame(
  gameId: string,
  playerName: string,
): Promise<GameState> {
  const db = getFirebaseDb();
  const uid = getCurrentUid();
  if (!uid) throw new Error('Not authenticated');

  // Read current state
  const snap = await get(ref(db, `games/${gameId}`));
  if (!snap.exists()) throw new Error('Game not found');

  const state = snap.val() as GameState;

  if (state.meta.status !== 'waiting') {
    throw new Error('Game already started');
  }

  if (state.meta.player2) {
    throw new Error('Game is full');
  }

  // Determine player 2's color (opposite of player 1)
  const p1Color = state.meta.player1?.color ?? 'black';
  const p2Color: PlayerColor = p1Color === 'black' ? 'red' : 'black';

  // Join the game
  const updates: Record<string, unknown> = {
    'meta/player2': {
      uid,
      name: playerName,
      color: p2Color,
    },
    'meta/status': 'playing',
  };

  await update(ref(db, `games/${gameId}`), updates);

  // Return updated state
  const updatedSnap = await get(ref(db, `games/${gameId}`));
  return updatedSnap.val() as GameState;
}

/** Subscribe to real-time game state changes */
export function subscribeToGame(
  gameId: string,
  callback: (state: GameState | null) => void,
): Unsubscribe {
  const db = getFirebaseDb();
  return onValue(ref(db, `games/${gameId}`), snap => {
    callback(snap.exists() ? (snap.val() as GameState) : null);
  });
}

/** Get current game state (one-time read) */
export async function getGameState(gameId: string): Promise<GameState | null> {
  const db = getFirebaseDb();
  const snap = await get(ref(db, `games/${gameId}`));
  return snap.exists() ? (snap.val() as GameState) : null;
}
